import { devError } from '../utils/devLogger';
import {
  getFirestore,
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  increment
} from 'firebase/firestore';

/**
 * Question Bank Service
 * Manages shared question repository for quiz building
 */
class QuestionBankService {
  constructor() {
    this.db = getFirestore();
    this.collectionName = 'questionBank';
  }

  /**
   * Add question to bank
   * @param {Object} questionData - Question data
   * @param {string} userId - User ID
   * @param {string} userName - User name
   * @returns {Promise<Object>} - Result with id
   */
  async addQuestion(questionData, userId, userName) {
    try {
      const docRef = await addDoc(collection(this.db, this.collectionName), {
        ...questionData,
        createdBy: userId,
        createdByName: userName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        usageCount: 0
      });

      return { success: true, id: docRef.id };
    } catch (error) {
      devError('Error adding question to bank:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update question in bank
   * @param {string} questionId - Question ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} - Result
   */
  async updateQuestion(questionId, updates) {
    try {
      await updateDoc(doc(this.db, this.collectionName, questionId), {
        ...updates,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      devError('Error updating question:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete question from bank
   * @param {string} questionId - Question ID
   * @returns {Promise<Object>} - Result
   */
  async deleteQuestion(questionId) {
    try {
      await deleteDoc(doc(this.db, this.collectionName, questionId));
      return { success: true };
    } catch (error) {
      devError('Error deleting question:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Search questions with filters
   * @param {Object} filters - Filter options
   * @returns {Promise<Array>} - Array of questions
   */
  async searchQuestions(filters = {}) {
    try {
      let q = collection(this.db, this.collectionName);
      const constraints = [];

      // Filter by category
      if (filters.category) {
        constraints.push(where('category', '==', filters.category));
      }

      // Filter by difficulty
      if (filters.difficulty) {
        constraints.push(where('difficulty', '==', filters.difficulty));
      }

      // Filter by question type
      if (filters.questionType) {
        constraints.push(where('questionType', '==', filters.questionType));
      }

      // Filter by creator
      if (filters.createdBy) {
        constraints.push(where('createdBy', '==', filters.createdBy));
      }

      // Filter by public/private
      if (filters.isPublic !== undefined) {
        constraints.push(where('isPublic', '==', filters.isPublic));
      }

      // Order by
      const orderField = filters.orderBy || 'createdAt';
      const orderDirection = filters.orderDirection || 'desc';
      constraints.push(orderBy(orderField, orderDirection));

      q = query(q, ...constraints);

      const snapshot = await getDocs(q);
      const questions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Client-side text search if provided
      if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        return questions.filter(q =>
          q.question.toLowerCase().includes(term) ||
          q.tags?.some(tag => tag.toLowerCase().includes(term))
        );
      }

      return questions;
    } catch (error) {
      devError('Error searching questions:', error);
      return [];
    }
  }

  /**
   * Get question by ID
   * @param {string} questionId - Question ID
   * @returns {Promise<Object|null>} - Question or null
   */
  async getQuestion(questionId) {
    try {
      const docSnap = await getDoc(doc(this.db, this.collectionName, questionId));
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      devError('Error getting question:', error);
      return null;
    }
  }

  /**
   * Increment usage count when question is added to quiz
   * @param {string} questionId - Question ID
   * @returns {Promise<void>}
   */
  async incrementUsage(questionId) {
    try {
      await updateDoc(doc(this.db, this.collectionName, questionId), {
        usageCount: increment(1),
        lastUsedAt: serverTimestamp()
      });
    } catch (error) {
      devError('Error incrementing usage:', error);
    }
  }

  /**
   * Get all categories
   * @returns {Promise<Array>} - Array of category names
   */
  async getCategories() {
    try {
      const questions = await this.searchQuestions({});
      const categories = [...new Set(questions.map(q => q.category).filter(c => c))];
      return categories.sort();
    } catch (error) {
      devError('Error getting categories:', error);
      return [];
    }
  }

  /**
   * Get all tags
   * @returns {Promise<Array>} - Array of tag names
   */
  async getTags() {
    try {
      const questions = await this.searchQuestions({});
      const allTags = questions.flatMap(q => q.tags || []);
      return [...new Set(allTags)].sort();
    } catch (error) {
      devError('Error getting tags:', error);
      return [];
    }
  }

  /**
   * Get user's questions
   * @param {string} userId - User ID
   * @returns {Promise<Array>} - Array of questions
   */
  async getUserQuestions(userId) {
    try {
      return await this.searchQuestions({ createdBy: userId });
    } catch (error) {
      devError('Error getting user questions:', error);
      return [];
    }
  }

  /**
   * Get popular questions (most used)
   * @param {number} limit - Number of questions to return
   * @returns {Promise<Array>} - Array of questions
   */
  async getPopularQuestions(limit = 10) {
    try {
      const questions = await this.searchQuestions({
        orderBy: 'usageCount',
        orderDirection: 'desc'
      });
      return questions.slice(0, limit);
    } catch (error) {
      devError('Error getting popular questions:', error);
      return [];
    }
  }

  /**
   * Get recent questions
   * @param {number} limit - Number of questions to return
   * @returns {Promise<Array>} - Array of questions
   */
  async getRecentQuestions(limit = 10) {
    try {
      const questions = await this.searchQuestions({
        orderBy: 'createdAt',
        orderDirection: 'desc'
      });
      return questions.slice(0, limit);
    } catch (error) {
      devError('Error getting recent questions:', error);
      return [];
    }
  }
}

export default QuestionBankService;
